import { ChevronLeft, X } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * The page frame every screen sits in.
 *
 * One gutter, one rhythm. Screens that set their own padding are how a product
 * ends up with four different left edges.
 */
export function Page({
  fill = false,
  className,
  ...props
}: React.ComponentProps<"div"> & { fill?: boolean }) {
  return (
    <div
      className={cn(
        // `relative` is load-bearing. Tailwind's `sr-only` is `position:
        // absolute`, and screens put them in buttons, menus and table rows.
        // With no positioned ancestor they resolve against the document: on the
        // member detail two of them landed at y=1531 and stretched <html> to
        // 1530px, so the whole page scrolled through 728 pixels of nothing.
        // Every popover is `fixed`, so none of them is caught by this.
        "relative mx-auto w-full max-w-[112rem] px-5 pt-6 md:px-8",
        // A screen whose content is a table fills the frame and lets the table
        // scroll inside it. Anything else flows and the column scrolls, which is
        // why the bottom padding only belongs to the second kind.
        fill ? "flex h-full min-h-0 flex-col pb-5" : "pb-16",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Title, a line of context, and the actions for this screen.
 *
 * Secondary actions sit to the left of the primary one and never compete with
 * it: exactly one filled button per page header, or none.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  back,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** A way back to where this page came from, rendered before the title. */
  back?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        "mb-6 flex flex-wrap items-start justify-between gap-x-6 gap-y-4",
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-2">
        {back}
        <div className="min-w-0">
          {eyebrow ? <p className="text-eyebrow text-fg-subtle mb-1.5">{eyebrow}</p> : null}
          <h1 className="text-title-1 text-heading text-balance">{title}</h1>
          {description ? (
            <p className="text-fg-muted text-callout mt-1.5 max-w-2xl text-pretty">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2.5">{actions}</div>
      ) : null}
    </header>
  );
}

/** A heading inside a page — a form section, a group of cards. */
export function SectionHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mb-3 flex items-end justify-between gap-4", className)}>
      <div className="min-w-0">
        <h2 className="text-title-3 text-heading">{title}</h2>
        {description ? (
          <p className="text-fg-muted text-subhead mt-1 text-pretty">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Search on the left, controls on the right, wrapping to two rows when narrow. */
export function Toolbar({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      // `shrink-0`: on a `fill` page the card is the one thing that gives when
      // the frame is short. A toolbar squeezed instead would drop its controls.
      className={cn("mb-4 flex shrink-0 flex-wrap items-center gap-2.5", className)}
      {...props}
    />
  );
}

/** Everything after this is pushed to the right edge of the toolbar. */
export function ToolbarSpacer() {
  return <div className="ml-auto" />;
}

/**
 * A chip for one filter that is currently narrowing the list.
 *
 * Filters that are applied but invisible are the single most common way a
 * table lies to someone. Every active one shows here, and every one can be
 * taken off from here.
 */
export function FilterChip({
  label,
  value,
  onRemove,
}: {
  label: ReactNode;
  value: ReactNode;
  onRemove: () => void;
}) {
  return (
    <span className="border-line bg-surface-raised text-subhead inline-flex max-w-full items-center gap-1.5 rounded-full border py-1 pr-1 pl-2.5">
      <span className="text-fg-muted shrink-0">{label}</span>
      <span className="text-fg min-w-0 truncate font-medium">{value}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove filter ${typeof label === "string" ? label : ""}`}
        className="text-fg-subtle hover:bg-surface-inset hover:text-fg grid size-5 shrink-0 place-items-center rounded-full transition-colors"
      >
        <X className="size-3" aria-hidden />
      </button>
    </span>
  );
}

/**
 * The back affordance for a detail page.
 *
 * Exported as a class as well as a component, because it is usually a router
 * `<Link>`: wrapping a button inside an anchor is two controls in one place,
 * and assistive technology has no good answer for that.
 */
export const backLinkClass =
  "text-fg-muted hover:bg-surface-inset hover:text-fg mt-0.5 -ml-1 grid size-8 shrink-0 place-items-center rounded-lg transition-colors";

export function BackLink({ children, ...props }: React.ComponentProps<"button">) {
  return (
    <button type="button" className={backLinkClass} {...props}>
      <BackArrow label={typeof children === "string" ? children : "Back"} />
    </button>
  );
}

/** The arrow itself, for a `<Link>` wearing `backLinkClass`. */
export function BackArrow({ label }: { label: string }) {
  return (
    <>
      <ChevronLeft className="size-5" aria-hidden />
      <span className="sr-only">{label}</span>
    </>
  );
}
